import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { FormatUTC } from 'shared/utils/formatUTC';
import { isDatePassed } from 'shared/utils/date';
import {
  Task,
  UnallocatedInvestment,
  getTasksQuery,
  getUnallocatedInvestmentsQuery,
} from './api';
import { Program } from '../Reports/NewReportDialog';
import { getPrograms } from '../ReportsBeta/NewReport/api';
import { getAllNPOUsers, NPOUser } from '../ReportsBeta/NewProgram/api';
import { getReportsForTodo, ReportTodo } from '../ReportsBeta/api';
import {
  buildUnallocatedInvestmentTasks,
  createTaskFromReport,
  getTaskByReportId,
  hasInterimDatePassed,
  isLegacyTask,
  isReportReadyForFinalUpdates,
  isReportReadyForInterimUpdates,
  TaskReport,
} from './utils';

export interface TasksBuckets {
  finalUpdateTasks: Task[];
  interimUpdateTasks: Task[];
  reportTasks: Task[];
  overdue: Task[];
  completed: Task[];
  incomplete: Task[];
  thisWeek: Task[];
  thisMonth: Task[];
  nextMonth: Task[];
  unallocatedInvestmentTasks: Task[];
}

const emptyTasks: TasksBuckets = {
  finalUpdateTasks: [],
  interimUpdateTasks: [],
  reportTasks: [],
  overdue: [],
  completed: [],
  incomplete: [],
  thisWeek: [],
  thisMonth: [],
  nextMonth: [],
  unallocatedInvestmentTasks: [],
};

const skipTaskReport = (report: ReportTodo['reportsTodo'][number]) => {
  if (report.version !== 'v4') {
    return true;
  }
  if (report.status === 'Interim Updates Needed') {
    return false;
  }
  if (report.status === 'Final Updates Needed') {
    return false;
  }
  if (report.status === 'Published') {
    if (report.stage === 'FINAL') {
      return true;
    }
    if (isDatePassed(report.endDate)) {
      return false;
    }
    return !hasInterimDatePassed(report);
  }
  return (
    report.status === 'TI Feedback Provided' &&
    report.feedbackType !== 'TI Only'
  );
};

const shouldIgnoreTask = (
  task: Task,
  reportTasks: Task[],
  usedReportIds: Set<number>,
) => {
  if (task.taskType === 'FINALIZE' || task.taskType === 'RESOLVE') {
    return true;
  }
  const report = task.allocation?.report;

  if (task.allocation?.report?.version === null) {
    return true;
  }

  if (task.taskType === 'ALLOCATE') {
    return true;
  }
  if (
    report?.enabled === false || report?.dbId
      ? usedReportIds.has(report.dbId)
      : false
  ) {
    return true;
  }

  if (task.taskType === 'PUBLISH' && report?.status === 'Published') {
    return true;
  }

  if (reportTasks.findIndex(x => x.taskId === task.taskId) !== -1) {
    return true;
  }

  return isLegacyTask(task);
};

type RequestStatus = ReturnType<typeof useGraphQLRequest>[0]['type'];

export interface TaskContextValue {
  tasks: TasksBuckets;
  taskReports: TaskReport;
  allNPOUsers: ReturnType<typeof useGraphQLRequest>[0] extends infer _
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ? any
    : never;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allPrograms: any;
  reloadTasks: () => void;
  reloadPrograms: () => ReturnType<typeof getPrograms> | undefined;
  reloadNPOUser: () => void;
  requestStatus: RequestStatus;
  reportsStatus: RequestStatus;
  unallocatedStatus: RequestStatus;
  hasError: boolean;
}

const TaskContext = createContext<TaskContextValue | undefined>(undefined);

export const TaskProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const client = useClient();
  const [taskReports, setTaskReports] = useState<TaskReport>({});
  const [tasks, setTasks] = useState<TasksBuckets>(emptyTasks);

  const [requestState, makeRequest] = useGraphQLRequest<{ allTasks: Task[] }>();
  const [reportsRequest, makeReportsRequest] = useGraphQLRequest<ReportTodo>();
  const [
    unallocatedInvestments,
    makeUnallocatedInvestments,
  ] = useGraphQLRequest<{
    unallocatedInvestments: UnallocatedInvestment[];
  }>();
  const [allNPOUsers, makeAllNPOUsersRequest] = useGraphQLRequest<{
    allUsers: NPOUser[];
  }>();
  const [allPrograms, makeAllProgramRequest] = useGraphQLRequest<{
    allPrograms: Program[];
  }>();

  const reloadTasks = useCallback(() => {
    if (!client) return;
    makeRequest(getTasksQuery(client));
    makeReportsRequest(getReportsForTodo(client));
    makeUnallocatedInvestments(getUnallocatedInvestmentsQuery(client));
  }, [makeRequest, makeReportsRequest, makeUnallocatedInvestments, client]);

  const reloadPrograms = useCallback(() => {
    if (!client) return;
    const request = getPrograms(client);
    makeAllProgramRequest(request);
    return request;
  }, [makeAllProgramRequest, client]);

  const reloadNPOUser = useCallback(() => {
    client && makeAllNPOUsersRequest(getAllNPOUsers(client));
  }, [makeAllNPOUsersRequest, client]);

  useEffect(() => {
    if (!client) return;
    reloadTasks();
    makeUnallocatedInvestments(getUnallocatedInvestmentsQuery(client));
  }, [reloadTasks, client]);

  useEffect(() => {
    if (
      requestState.type !== 'REQUEST_SUCCESS' ||
      reportsRequest.type !== 'REQUEST_SUCCESS' ||
      unallocatedInvestments.type !== 'REQUEST_SUCCESS'
    ) {
      return;
    }

    const reportTasks: Task[] = [];
    const finalUpdateTasks: Task[] = [];
    const interimUpdateTasks: Task[] = [];
    const allTasks = requestState.data.allTasks;
    const tasksByReportId = getTaskByReportId(allTasks);
    const usedReportIds = new Set<number>();
    const nextTaskReports: TaskReport = {};

    for (const report of reportsRequest.data.reportsTodo) {
      if (skipTaskReport(report)) {
        continue;
      }
      usedReportIds.add(report.dbId);
      const task = tasksByReportId[report.dbId];
      const isFinalUpdateTask = isReportReadyForFinalUpdates(report);
      const isInterimUpdateTask = isReportReadyForInterimUpdates(report);
      const t = createTaskFromReport(
        task,
        report,
        isFinalUpdateTask
          ? 'finalUpdate'
          : isInterimUpdateTask
          ? 'interimUpdate'
          : 'feedback',
      );
      nextTaskReports[t.taskId] = { report, task: t };
      if (isFinalUpdateTask) {
        finalUpdateTasks.push(t);
      } else if (isInterimUpdateTask) {
        interimUpdateTasks.push(t);
      } else {
        reportTasks.push(t);
      }
    }
    setTaskReports(nextTaskReports);

    const overdue: Task[] = [];
    const completed: Task[] = [];
    const incomplete: Task[] = [];
    const thisWeek: Task[] = [];
    const thisMonth: Task[] = [];
    const nextMonth: Task[] = [];
    const unallocatedInvestmentTasks: Task[] = buildUnallocatedInvestmentTasks(
      unallocatedInvestments.data.unallocatedInvestments,
    );

    for (const task of allTasks) {
      if (
        shouldIgnoreTask(
          task,
          [...reportTasks, ...finalUpdateTasks, ...interimUpdateTasks],
          usedReportIds,
        )
      ) {
        continue;
      }

      switch (task.status) {
        case 'OVERDUE':
          overdue.push(task);
          break;
        case 'COMPLETED':
          completed.push(task);
          break;
        case 'UNCOMPLETED': {
          const date = new FormatUTC(task.dueDate);
          if (date.thisWeek()) {
            thisWeek.push(task);
          } else if (date.thisMonth()) {
            thisMonth.push(task);
          } else if (date.nextMonth()) {
            nextMonth.push(task);
          } else {
            incomplete.push(task);
          }
          break;
        }
      }
    }

    setTasks({
      finalUpdateTasks,
      reportTasks,
      overdue,
      completed,
      incomplete,
      interimUpdateTasks,
      thisWeek: overdue.concat(thisWeek),
      thisMonth,
      nextMonth,
      unallocatedInvestmentTasks,
    });
  }, [requestState, reportsRequest, unallocatedInvestments]);

  const value = useMemo<TaskContextValue>(
    () => ({
      tasks,
      taskReports,
      allNPOUsers,
      allPrograms,
      reloadTasks,
      reloadPrograms,
      reloadNPOUser,
      requestStatus: requestState.type,
      reportsStatus: reportsRequest.type,
      unallocatedStatus: unallocatedInvestments.type,
      hasError: requestState.type === 'REQUEST_ERROR',
    }),
    [
      tasks,
      taskReports,
      allNPOUsers,
      allPrograms,
      reloadTasks,
      reloadPrograms,
      reloadNPOUser,
      requestState.type,
      reportsRequest.type,
      unallocatedInvestments.type,
    ],
  );

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

export const useTaskContext = (): TaskContextValue => {
  const ctx = useContext(TaskContext);
  if (!ctx) {
    throw new Error('useTaskContext must be used within a TaskProvider');
  }
  return ctx;
};
